import type { CustomTheme } from "../api/types";
import { customThemeRef } from "../lib/palette";
import { THEMES } from "../lib/themes";

/**
 * The palette chooser.
 *
 * Each swatch carries its own `data-theme`, so the dots inside it are painted
 * by that theme's own CSS block — the preview is the real palette rather than a
 * second copy of it kept in TypeScript. See styles/themes.css.
 *
 * A palette the member built works exactly the same way: AuthContext renders
 * every one of them into a stylesheet with the same unscoped selector shape, so
 * a custom swatch previews itself with no special case here beyond the edit
 * button.
 */
export function ThemePicker({
  value,
  onPick,
  customThemes,
  onEdit,
  disabled = false,
}: {
  value: string;
  onPick: (theme: string) => void;
  customThemes: CustomTheme[];
  /** Opens the editor for one of the member's own palettes. */
  onEdit?: (theme: CustomTheme) => void;
  disabled?: boolean;
}) {
  return (
    <div className="swatch-grid">
      {THEMES.map((theme) => (
        <button
          key={theme.id}
          type="button"
          data-theme={theme.id}
          className={`swatch ${value === theme.id ? "swatch--on" : ""}`}
          aria-pressed={value === theme.id}
          disabled={disabled}
          onClick={() => onPick(theme.id)}
        >
          <span className="swatch__dots">
            <span className="swatch__dot swatch__dot--brand" />
            <span className="swatch__dot swatch__dot--spend" />
            <span className="swatch__dot swatch__dot--save" />
          </span>
          <span className="swatch__name">{theme.name}</span>
          <span className="swatch__blurb">{theme.blurb}</span>
        </button>
      ))}

      {customThemes.map((theme) => {
        const ref = customThemeRef(theme.id);
        return (
          <div key={theme.id} className="swatch-slot">
            <button
              type="button"
              data-theme={ref}
              className={`swatch ${value === ref ? "swatch--on" : ""}`}
              aria-pressed={value === ref}
              disabled={disabled}
              onClick={() => onPick(ref)}
            >
              <span className="swatch__dots">
                <span className="swatch__dot swatch__dot--brand" />
                <span className="swatch__dot swatch__dot--spend" />
                <span className="swatch__dot swatch__dot--save" />
              </span>
              <span className="swatch__name">{theme.name}</span>
              <span className="swatch__blurb">Yours</span>
            </button>

            {onEdit ? (
              <button
                type="button"
                className="swatch__edit"
                disabled={disabled}
                onClick={() => onEdit(theme)}
                aria-label={`Edit ${theme.name}`}
              >
                ✏️
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
